// apps/web/src/routes/settings-auto-reply.tsx
import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { Textarea } from '@viecxanh/chat-ui/textarea'
import { Button } from '@viecxanh/chat-ui/button'
import { Switch } from '@viecxanh/chat-ui/switch'
import { Skeleton } from '@viecxanh/chat-ui/skeleton'
import { api } from '../lib/api-client'
import type { AutoReplyConfig } from '@viecxanh/chat-shared'

const CHANNELS = [
  { key: 'all', label: 'Tất cả kênh' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'zalo_oa', label: 'Zalo OA' },
  { key: 'widget', label: 'Widget' },
  { key: 'internal', label: 'Nội bộ' },
]

export function AutoReplyPage() {
  const { data, isLoading, mutate } = useSWR<AutoReplyConfig[]>(
    '/api/admin/settings/auto-reply',
    (url: string) => api.get<AutoReplyConfig[]>(url),
  )

  const [configs, setConfigs] = useState<AutoReplyConfig[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (data) setConfigs(data)
  }, [data])

  const update = (channel: string, patch: Partial<AutoReplyConfig>) => {
    setConfigs((prev) => prev.map((c) => (c.channel === channel ? { ...c, ...patch } : c)))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.put('/api/admin/settings/auto-reply', { configs })
      await mutate()
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <Skeleton className="h-64 w-full" />

  return (
    <div className="space-y-6 max-w-xl">
      <h2 className="text-lg font-semibold">Tự động trả lời ngoài giờ</h2>
      <p className="text-sm text-slate-500">
        Tin nhắn gửi tự động khi khách nhắn ngoài giờ làm việc đã cấu hình.
      </p>

      {configs.map((cfg) => {
        const label = CHANNELS.find((c) => c.key === cfg.channel)?.label ?? cfg.channel
        return (
          <div key={cfg.channel} className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">{label}</span>
              <Switch checked={cfg.is_active} onCheckedChange={(v) => update(cfg.channel, { is_active: v })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Tin nhắn chào (luôn gửi khi tạo hội thoại mới)</label>
              <Textarea
                value={cfg.welcome_message ?? ''}
                disabled={!cfg.is_active}
                onChange={(e) => update(cfg.channel, { welcome_message: e.target.value })}
                rows={2}
                className="disabled:opacity-50"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Tin nhắn ngoài giờ</label>
              <Textarea
                value={cfg.message}
                disabled={!cfg.is_active}
                onChange={(e) => update(cfg.channel, { message: e.target.value })}
                rows={3}
                className="disabled:opacity-50"
              />
            </div>
          </div>
        )
      })}

      <Button onClick={() => void handleSave()} disabled={saving} className="bg-brand hover:bg-brand-dark">
        {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
      </Button>
    </div>
  )
}
